<div class="vault-page-shell">
  <div class="space-y-6" *ngIf="!submitSuccess; else requestSuccess">
    <a [routerLink]="'/dashboard/accounts'" class="vault-back-link">
      <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
      </svg>
      Back to accounts
    </a>

    <div class="vault-page-header">
      <div class="space-y-3">
        <p class="vault-page-kicker">Support</p>
        <div class="space-y-2">
          <h1 class="vault-page-title">Report an issue</h1>
          <p class="vault-page-copy max-w-2xl">
            Share what happened while adding a provider through TEFCA and our team will investigate the connection flow.
          </p>
        </div>
      </div>
    </div>
    <div class="vault-divider"></div>

    <div class="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
      <section class="vault-panel p-6 sm:p-8">
        <form class="space-y-8" (ngSubmit)="submitForm()" #supportRequestForm="ngForm">
          <div class="space-y-2">
            <h2 class="vault-card-title">Issue details</h2>
            <p class="vault-card-copy">
              Tell us what happened while adding a provider so we can investigate the TEFCA connection flow and follow up with next steps.
            </p>
          </div>

          <div *ngIf="errorMsg" class="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">
            <p class="font-semibold text-red-800">We couldn’t submit your request.</p>
            <p class="mt-1">{{ errorMsg }}</p>
          </div>

          <div class="grid gap-5 md:grid-cols-2">
            <div>
              <label class="vault-field-label" for="support-email">
                Your email address <span class="text-red-500">*</span>
              </label>
              <input
                [(ngModel)]="formSupportRequest.email"
                class="vault-input"
                id="support-email"
                name="email"
                placeholder="you@example.com"
                required
                type="email">
            </div>

            <div>
              <label class="vault-field-label" for="support-health-system">
                Health system name <span class="text-red-500">*</span>
              </label>
              <input
                [(ngModel)]="formSupportRequest.healthsystem_name"
                class="vault-input"
                id="support-health-system"
                name="healthsystem_name"
                placeholder="Mayo Clinic, Cleveland Clinic, Kaiser Permanente"
                required
                type="text">
            </div>
          </div>

          <div>
            <label class="vault-field-label" for="support-description">
              Describe your issue <span class="text-red-500">*</span>
            </label>
            <textarea
              [(ngModel)]="formSupportRequest.request_content"
              class="vault-input min-h-[220px]"
              id="support-description"
              name="request_content"
              placeholder="Please share the provider, what you clicked, what page you were on, and the error or behavior you saw."
              required></textarea>
          </div>

          <div class="flex justify-end">
            <button
              [disabled]="!supportRequestForm.form.valid || loading"
              class="vault-primary-button min-w-[220px] justify-center px-5 py-3 disabled:cursor-not-allowed disabled:opacity-60"
              type="submit">
              <app-spinner *ngIf="loading" [sizePx]="18" [strokeWidthPx]="2.5" accentColor="#ffffff" trackColor="rgba(255,255,255,0.35)"></app-spinner>
              Submit request
            </button>
          </div>
        </form>
      </section>

      <aside class="vault-panel p-6 sm:p-8">
        <div class="space-y-6">
          <div class="rounded-lg bg-[#EEF2FF] p-5">
            <p class="text-sm font-semibold uppercase tracking-[0.18em] text-[#5B47FB]">Support</p>
            <h2 class="vault-card-title mt-3">Helpful context speeds things up</h2>
            <p class="vault-card-copy mt-2">
              Include the provider name, what step failed, and what you expected to happen so we can trace the TEFCA handoff faster.
            </p>
          </div>

          <div *ngIf="error || error_description" class="rounded-lg border border-amber-200 bg-amber-50 p-5">
            <div class="space-y-3">
              <div class="space-y-1">
                <h3 class="font-semibold text-amber-900">Connection details detected</h3>
                <p class="text-sm text-amber-800">We’ll include this technical context with your request.</p>
              </div>
              <div class="space-y-1 text-sm text-amber-800">
                <p *ngIf="error"><span class="font-medium">Type:</span> {{ error }}</p>
                <p *ngIf="error_description"><span class="font-medium">Description:</span> {{ error_description }}</p>
              </div>
            </div>
          </div>

          <div class="rounded-lg bg-slate-50 p-5">
            <div class="space-y-3">
              <h3 class="font-semibold text-slate-900">What to include</h3>
              <div class="space-y-2 text-sm text-slate-600">
                <p>The provider or portal you were trying to connect.</p>
                <p>The step where the flow stopped or redirected unexpectedly.</p>
                <p>Any error message, code, or unusual behavior you noticed.</p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </div>
</div>

<ng-template #requestSuccess>
  <div class="mx-auto max-w-2xl">
    <section class="vault-panel px-6 py-10 text-center sm:px-10 sm:py-12">
      <div class="space-y-6">
        <div class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#5B47FB]/10">
          <svg class="h-8 w-8 text-[#5B47FB]" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <div class="space-y-3">
          <p class="vault-page-kicker">Request submitted</p>
          <h2 class="text-3xl font-semibold tracking-tight text-slate-900">Thanks, we’ve got it</h2>
          <p class="mx-auto max-w-lg text-sm leading-7 text-slate-500">
            Your support request has been recorded. We’ll review the issue and follow up when we have an update.
          </p>
        </div>

        <div class="flex justify-center">
          <button (click)="dismiss()" class="vault-primary-button px-5 py-3" type="button">
            Back to accounts
          </button>
        </div>
      </div>
    </section>
  </div>
</ng-template>
